import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { TYPE_COLOR_MAP, type CalendarEventDto, type CalendarEventType } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { formatTime } from '../../lib/base-i18n';
import { layoutWeek, daysBetween, fitOverflowIntoLastLane } from './lib/monthLayout';
import { TypeIcon } from './calendarTypeMeta';
import { EventCreatorAvatar } from './EventCreatorAvatar';
import { ReservationBadge } from './ReservationBadge';
import { useEventTooltip } from './eventTooltip';
import { useMyUserId, isMineEvent } from './lib/eventOwnership';
import { isDragBlocked } from './dragPolicy';
import { useContinuousWeeks, WEEK_ROW_HEIGHT_PX } from './useContinuousWeeks';
import { longEventFill, MONTH_LONG_EVENT_FILL_PCT } from './lib/longEventFill';
import { Plus } from '../../icons';

interface MonthViewProps {
  cursor: Date;  // month-start (1일 00:00).
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  // 날짜 셀 클릭 → 그 날짜로 (CalendarPage 가 레이어 팝업/생성 모달 결정). anchor=클릭 셀 위치.
  onDateClick?: (date: Date, anchor?: DOMRect) => void;
  // 공휴일 레이어(옵셔널, 회귀 안전). date=YYYY-MM-DD.
  holidays?: Array<{ date: string; name: string; isSubstitute: boolean }>;
  // 캘린더 3열 셸 — 우측 레일이 보여주는 선택일 강조(옵셔널). 미전달 시 기존과 동일(강조 없음).
  selectedDate?: Date | null;
  /**
   * ⭐ **연속 창이 새 구간을 필요로 할 때** — 그 구간의 일정을 채워 넣는 일은 호출부가 한다 (M-1).
   *
   * ⛔ **옵셔널이 아니다.** 안 넘기면 스크롤해서 나온 주들이 «빈 칸»으로 보인다 —
   *   화면은 멀쩡한데 일정만 없는 꼴이라 「일정이 사라졌다」로 읽힌다.
   *   ⇒ ★ 그래서 타입이 «반드시 넘기게» 만든다. 주간(`TimeGridStream`)도 같은 자리를 그렇게 둔다.
   */
  onNeedRange: (from: Date, to: Date) => void;
  /**
   * ⭐ **화면 위에 걸린 달이 바뀔 때** — 헤더 제목을 그 달로 맞추라는 알림 (M-2 · 옵셔널).
   * ⚠ 주간의 `onVisibleDayChange` 와 «같은 자리·같은 까닭»이다 — 스크롤로 다른 달이 보이는데
   *   제목이 `cursor` 의 달에 머물면 **어긋나 보인다**(주간이 그 리포트를 받았다).
   */
  onVisibleMonthChange?: (year: number, month: number) => void;
  /**
   * ⭐ **일정을 다른 날로 «끌어 옮긴다»** (M-4 · 사용자 지시 #39
   *   「월간·주간 달력에서 드래그를 이용해 일정이 이동할 수 있도록」).
   *
   * 날짜 칸에 놓으면 **그 날짜까지의 일수 × 1440(분)** 을 넘긴다.
   * ⇒ ★ 주간(`TimeGridView.onEventMove`)과 **꼴이 같다** ⇒ `CalendarPage` 의 `handleEventMove` 를
   *   **그대로** 쓴다 — 반복 일정의 «범위 묻기»와 되돌리기가 이미 그 안에 있다.
   * ⚠ 미지정이면 **드래그 자체가 꺼진다**(`canDrag` 가 false 라 `draggable` 이 안 붙는다)
   *   ⇒ 종전 동작과 100% 같다.
   */
  onEventMove?: (e: CalendarEventDto, deltaMinutes: number) => void;
  /**
   * ⭐ 날짜 칸 호버 «＋» — 그 날짜로 «바로» 만들기 창을 연다 (옛 가지 7655e3bd1 · 2026-10-13 이식).
   *   3열 셸에서는 칸을 누르면 «선택»만 되므로(우측 레일), 만들기까지 한 단계가 더 있었다.
   * ⚠ 미지정이면 단추 자체를 안 그린다 ⇒ 종전과 100% 같다.
   */
  onQuickCreate?: (date: Date) => void;
}

export interface MonthViewHandle {
  /**
   * ⭐ 임의 날짜로 «미끄러져 간다» — 창 «안»이면 스크롤만, 창 «밖»이면 창을 다시 만든다.
   *
   * ⚠ **그래서 멀리 뛰면 «즉시 뛴 것»처럼 보인다.** 창이 최대 24주(약 5~6개월)라, 그 밖으로
   *   가면 훅이 창을 새로 지어 그 자리에 놓는다 — 미끄러질 중간이 없기 때문이다.
   *   ⇒ ⛔ **「애니메이션이 안 먹는 버그」가 아니다.** 사용자 결정(2026-10-02)은
   *     「ⓐ 그 달로 미끄러져 간다」이고, 그 경계는 이 구조의 값이다.
   */
  scrollToDate: (date: Date) => void;
}

const MAX_LANES = 4;
/**
 * ⭐ 2026-10-19 — 달력 영역이 넉넉하면 5줄(사용자 결정 「해상도에 따라 다르게」).
 *   기준은 해상도 숫자가 아니라 «달력 영역의 실제 높이»다 — 해상도·Windows 배율·브라우저 확대·창 크기를 한 번에 반영한다.
 *   6주(가장 긴 달)를 5줄 칸(134px)으로 다 담을 수 있을 때만 5줄 — 노트북(영역 약 500~740px)은 4줄 그대로.
 * 칸 높이: 4줄은 종전 그대로 WEEK_ROW_HEIGHT_PX(120 · 아래 7px 여유). 5줄은 날짜 줄 27 + 줄 5 × 19 + 사이 4 × 2 + 아래 4 = 134.
 * ⚠ 4줄을 공식(113)으로 세면 실제 칸(120)과 어긋나 연속 스크롤 셈이 한 주씩 밀린다(실측으로 잡았다) — 그래서 4줄은 상수다.
 */
const ROOMY_LANES = 5;
const weekHeightFor = (lanes: number) =>
  lanes <= MAX_LANES ? WEEK_ROW_HEIGHT_PX : 27 + lanes * 19 + (lanes - 1) * 2 + 4;
const ROOMY_MIN_GRID_PX = 6 * weekHeightFor(ROOMY_LANES);

/**
 * 공휴일명 글자 수 임계값 — 이보다 길면 글자를 한 단 줄인다 (M-3 ⓑ · 2026-10-06 사용자 「진행」).
 *
 * ## 원문과 «내가 읽은 범위»
 *
 * 2026-08-25 사용자 지시는 한 줄이다:
 * ```
 * 「모든 공휴일명을 줄이라는 게 아니라 칸을 늘려야 하는 수준의 길이면 줄이라는 것」
 * ```
 * ⛔ **그 말에는 «범위»가 없다** — 월간만인지 주간·일간도인지 안 적혀 있다.
 * ⇒ ★ **「월간만」으로 읽었다**(관제 권고). 까닭: 칸이 작아 이름이 넘치는 것은 **월간**에서
 *   생긴다(월간 칸은 그리드의 1/7). 주간·일간은 한 줄에 한 날이라 넘칠 폭이 아니다.
 * ⇒ ⚠ **틀리면 사용자가 고친다** — 그래서 「그렇게 읽었다」를 여기 적어 둔다. 안 적으면
 *   다음 사람이 「주간에도 넣어야 하나」를 또 처음부터 묻는다.
 *
 * ## 값 셋은 «지어낸 것이 아니다»
 *
 * 옛 가지(`feature/dev-kyeongeun`)에 그 판이 있었고 그대로 가져왔다:
 * ```
 * acb2b787  긴 이름«에만» 적용            ⇒ 짧은 이름(광복절 3자)은 원래 크기 그대로
 * e87ac238  임계값 4자 → 7자 (사용자 조정) ⇒ 「대체공휴일」(5자)은 안 줄인다
 * 8afd1d6c  8px → 9px (너무 작아 보임)     ⇒ 10px → 9px 한 단
 * ```
 * ⚠ 글자 크기를 px 로 적는 것은 이 파일의 관습이다 — `tokens.css` 의 가장 작은 글자 토큰이
 *   `--font-xs: 0.75rem`(=12px)이라 **10px·9px 를 토큰으로 쓸 수가 없다**(실측).
 */
const HOLIDAY_LONG_NAME_THRESHOLD = 7;

/**
 * MonthView (캘린더 상세화 2026-06-07 S5) — 7x6 월간 뷰, 주(week) 단위 멀티데이 가로 바.
 *
 * 주별로 layoutWeek 가 멀티데이/종일 이벤트를 가로 바 레인에, 단일 이벤트를 한 열 칩에 배치.
 * 배경 셀(.cmw-bg)은 날짜·공휴일·클릭타깃, 전경(.cmw-fg)은 이벤트 바(겹침 회피 레인).
 * 빈 영역 클릭은 fg pointer-events:none 로 bg 셀에 전달 → onDateClick(레이어 팝업).
 */
export const MonthView = forwardRef<MonthViewHandle, MonthViewProps>(function MonthView(
  { cursor, events, loading, onEventClick, onDateClick, holidays, selectedDate, onNeedRange, onVisibleMonthChange, onEventMove, onQuickCreate },
  ref,
) {
  // 「내 일정은 굵게」 — 판정은 lib/eventOwnership 한 곳에 있다(프롭을 새로 뚫지 않는다).
  const myUserId = useMyUserId();
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  // 칩 툴팁(2026-09-09) — Provider 밖이면 빈 객체라 기존 렌더와 같다.
  const tooltipOf = useEventTooltip();

  const holidayByDate = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays ?? []) {
      // 방어: holidays 배열에 undefined/형식불량 원소가 섞여도 안전(공휴일은 비치명적 표시 레이어).
      if (!h || typeof h.date !== 'string') continue;
      if (!m.has(h.date)) m.set(h.date, h.name);
    }
    return m;
  }, [holidays]);

  /**
   * ⭐⭐ **M-1 — 「고정 6주 격자」를 «연속 스크롤 창»으로 갈아탔다** (2026-10-02).
   *
   * 종전에는 `cursor` 의 달에서 6주 × 7일을 «손으로» 만들었다. 이제 **주 목록을
   * `useContinuousWeeks` 가 준다** — 처음 13주(그 날짜가 속한 주 ± 6주)를 만들고, 가장자리에
   * 닿으면 6주씩 붙이며, 24주를 넘으면 반대쪽을 잘라낸다.
   *
   * ⇒ ★ **그리는 코드는 한 줄도 안 바꿨다.** 바뀐 것은 ⭐ 「주를 «어디서» 받나」뿐이다.
   *   (정본 `MonthView` 를 통째로 베끼지 «않았다» — 베끼면 main 에 없는 기능 셋
   *    「＋ 빠른 만들기」·「＋N 더 보기 라벨」·칸 라벨이 따라온다.
   *    ⇒ 그 가운데 「＋ 빠른 만들기」는 2026-10-13 판이 `onQuickCreate` 로 따로 가져왔다.)
   *
   * ⚠ 훅은 `Date[]`(주의 «시작일»)를 준다 — 종전 코드가 쓰던 `Date[][]`(7일 한 줄)로 편다.
   * ⚠ ⛔ **스크롤러는 `.calendar-month-grid` «자신»이다.** 겉을 한 겹 더 씌우면 그 안쪽이
   *   계속 스크롤러로 남아 **`onScroll` 이 영영 안 불린다** — 주간이 그 함정을 겪었고
   *   (`TimeGridStream.css` 머리말), **jsdom 이라 시험은 전부 초록이었다.**
   *   ⇒ ★ 그래서 ref·onScroll 을 **그 격자에 바로** 건다.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  /**
   * 「지금 화면 위에 걸린 달」 — 연속 창에는 «이번 달 격자»라는 것이 없다.
   * ⇒ ⚠ 종전의 흐림 규칙(`.calendar-month-cell--other`)이 **그대로 뜻을 가지려면** 기준이 필요하다.
   *   안 바꾸면 `cursor` 의 달만 진하고 **나머지 열 주가 통째로 흐려진다**(회귀).
   * ⇒ ★ 그래서 훅이 알려 주는 «상단에 걸린 달»을 기준으로 삼는다. 처음 값은 `cursor` 다.
   */
  const [visibleMonth, setVisibleMonth] = useState(() => ({ y: cursor.getFullYear(), m: cursor.getMonth() }));
  /* 달력 영역 높이를 재서 줄 수를 정한다 — 창 크기·확대가 바뀌면 그 자리에서 다시 정한다. */
  const [lanes, setLanes] = useState(MAX_LANES);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const apply = () => setLanes(el.clientHeight >= ROOMY_MIN_GRID_PX ? ROOMY_LANES : MAX_LANES);
    apply();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const weekH = weekHeightFor(lanes);

  const { weeks: weekStarts, onScroll, scrollToDate } = useContinuousWeeks({
    containerRef: gridRef,
    rowHeight: weekH,
    initialDate: cursor,
    onNeedRange,
    onVisibleMonthChange: (y, m) => {
      setVisibleMonth({ y, m });
      onVisibleMonthChange?.(y, m);
    },
  });

  /**
   * ⭐ **M-2 — 「이전/다음·오늘」이 «미끄러져 간다»** (2026-10-02 사용자 결정 ⓐ).
   *
   * ⛔ 부르는 쪽이 `cursor` 를 바꿔 **다시 마운트**하면 안 된다 — 그러면 스크롤로 쌓아 둔
   *   날짜 창이 매번 버려지고, 미끄러짐도 없다. ⇒ ★ 그래서 **ref 로 뛴다.**
   *   (주간이 같은 자리에서 같은 꼴을 쓴다 — `CalendarPage.tsx` 의 `streamRef`)
   */
  useImperativeHandle(ref, () => ({ scrollToDate }), [scrollToDate]);

  const weeks = useMemo(() => weekStarts.map((weekStart) => {
    const row: Date[] = [];
    for (let d = 0; d < 7; d++) {
      const dd = new Date(weekStart);
      dd.setDate(weekStart.getDate() + d);
      row.push(dd);
    }
    return row;
  }), [weekStarts]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthIdx = visibleMonth.m;
  const yearIdx = visibleMonth.y;
  const dayNames = [
    tCalendar('calendar.month.dayname.sun'),
    tCalendar('calendar.month.dayname.mon'),
    tCalendar('calendar.month.dayname.tue'),
    tCalendar('calendar.month.dayname.wed'),
    tCalendar('calendar.month.dayname.thu'),
    tCalendar('calendar.month.dayname.fri'),
    tCalendar('calendar.month.dayname.sat'),
  ];

  /**
   * ⭐ **끌고 있는 일정** — ref 로 든다(정본 그대로).
   *
   * ⚠ 상태가 아니라 ref 인 까닭: 끄는 «동안» 미리보기 강조를 하지 않으므로 **다시 그릴 것이
   *   없다.** 상태로 들면 끌 때마다 월간 전체가 다시 그려진다. (필요해지면 그때 상태로.)
   * ⚠ 그리고 월간은 주간과 달리 **픽셀→분**을 계산하지 않는다 — **날짜 칸 자체가 놓는 자리**라
   *   훨씬 단순하다.
   */
  const draggingEventRef = useRef<CalendarEventDto | null>(null);
  function handleDropOnDay(targetDate: Date, ev: React.DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    const dragging = draggingEventRef.current;
    draggingEventRef.current = null;
    if (!dragging || !onEventMove) return;
    const deltaMinutes = daysBetween(new Date(dragging.startAt), targetDate) * 1440;
    if (deltaMinutes === 0) return;   // ⭐ 원래 날짜에 도로 놓았으면 «아무 일도 하지 않는다»
    onEventMove(dragging, deltaMinutes);
  }

  function dateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function fireDateClick(d: Date, ev: React.MouseEvent) {
    if (!onDateClick) return;
    ev.stopPropagation();
    const dd = new Date(d); dd.setHours(9, 0, 0, 0);
    onDateClick(dd, (ev.currentTarget as HTMLElement).getBoundingClientRect());
  }

  return (
    <div className="calendar-month" aria-busy={loading}>
      <div className="calendar-month-header" role="row">
        {dayNames.map((d, i) => (
          <div
            key={d}
            role="columnheader"
            className={`calendar-month-dayname ${i === 0 ? 'calendar-month-dayname--sun' : i === 6 ? 'calendar-month-dayname--sat' : ''}`}
          >
            {d}
          </div>
        ))}
      </div>

      <div
        className="calendar-month-grid"
        role="grid"
        ref={gridRef}
        onScroll={onScroll}
        // 주 칸 높이(CSS .calendar-month-week 가 읽는다) — 4줄이면 종전 120px 그대로.
        style={lanes !== MAX_LANES ? ({ ['--cal-month-week-h' as string]: `${weekH}px` } as React.CSSProperties) : undefined}
      >
        {weeks.map((week) => {
          // ⭐ 2026-10-19 — 넘치는 날은 마지막 줄 자리를 「+n개」로(fitOverflowIntoLastLane 머리말).
          const { segments, more } = fitOverflowIntoLastLane(layoutWeek(week[0]!, events, lanes), lanes);
          /*
           * ⛔ key 를 «순서(index)»로 두면 안 된다 — 창은 위쪽에도 주를 «붙인다».
           *   그러면 모든 주의 index 가 밀려 React 가 ★ 옛 행을 새 날짜에 다시 쓴다
           *   (스크롤 위치·칩 상태가 엉뚱한 주에 남는다). ⇒ ⭐ 날짜로 못 박는다.
           */
          return (
            <div className="calendar-month-week" role="row" key={week[0]!.getTime()}>
              {/* 배경 셀 — 날짜/공휴일/클릭타깃 */}
              <div className="calendar-month-week-bg">
                {week.map((d, ci) => {
                  // ⚠ 해를 함께 본다 — 창이 24주(약 6개월)라 같은 달 번호가 두 해에 걸칠 일은
                  //   없지만, 기준이 「상단에 걸린 달」로 바뀌었으니 뜻을 글자로 못 박아 둔다.
                  const isOtherMonth = d.getMonth() !== monthIdx || d.getFullYear() !== yearIdx;
                  const isToday = d.getTime() === today.getTime();
                  const holidayName = holidayByDate.get(dateKey(d));
                  const isHoliday = holidayName != null;
                  const dow = d.getDay();
                  // 3열 셸 — selectedDate 미전달 시 항상 false → 기존 className 결과 동일.
                  const isSelected = selectedDate != null && dateKey(d) === dateKey(selectedDate);
                  return (
                    <div
                      key={ci}
                      role="gridcell"
                      className={`calendar-month-cell ${isOtherMonth ? 'calendar-month-cell--other-month' : ''} ${isToday ? 'calendar-month-cell--today' : ''} ${isHoliday ? 'calendar-month-cell--holiday' : ''} ${onDateClick ? 'calendar-month-cell--clickable' : ''} ${isSelected ? 'calendar-month-cell--selected' : ''}`}
                      onClick={(ev) => { if (ev.target === ev.currentTarget) fireDateClick(d, ev); }}
                      /*
                        ⭐ M-4 — 날짜 «칸»이 놓는 자리다.
                        ⚠ `onDragOver` 에서 `preventDefault()` 를 안 하면 브라우저가 ⛔ «놓기를 거부»한다
                          (HTML5 DnD 의 규칙이다) ⇒ 끌고 있을 때만 막는다.
                      */
                      onDragOver={(ev) => { if (draggingEventRef.current) ev.preventDefault(); }}
                      onDrop={(ev) => handleDropOnDay(d, ev)}
                    >
                      {/*
                        ⭐ 2026-10-16 — 공휴일 이름을 날짜 숫자 «옆 같은 줄»에 둔다(6100 · 옛 가지와 같은 자리 — 사용자 지시).
                          아래 줄에 쌓으면 그 줄만큼 일정 막대와 겹친다(옛 가지 2026-08-24 의 까닭). 좁으면 이름만 말줄임된다.
                      */}
                      <div className="calendar-month-cell-daterow">
                      <div
                        className={`calendar-month-cell-date ${dow === 0 || isHoliday ? 'calendar-month-cell-date--sun' : dow === 6 ? 'calendar-month-cell-date--sat' : ''}`}
                        onClick={(ev) => fireDateClick(d, ev)}
                      >
                        {d.getDate()}
                      </div>
                      {/*
                        ✅ **`title` 툴팁을 지웠다** (2026-10-07 · 사용자 승인 ＋ 원 결정 `173244f4`).

                        ① 사슬이 다 이어졌다 — 그래서 지울 수 있다.
                           2026-09-28 에 이 자리를 「중복처럼 보이지만 지우지 않았다」로 두었고,
                           만료 조건을 「긴 이름을 «줄여서 칸에 맞추는» 장치가 들어오면」으로 적고
                           그 장치를 **넷**으로 열거했다(옛 가지 `feature/dev-kyeongeun`):
                           ```
                           ✅ 88cdc1ee  칸 너비 — .calendar-month-week-bg .calendar-month-cell
                                        에 min-width: 0   ← 2026-10-07 (이 판의 첫 커밋)
                           ✅ acb2b787  긴 이름«에만» 축소        ← 2026-10-06 (M-3 ⓑ · #2220)
                           ✅ e87ac238  임계값 4자 → 7자          ← 같은 판
                           ✅ 8afd1d6c  8px → 9px                ← 같은 판
                           ```
                           ⇒ ★ **넷이 다 섰다.** 원 결정은 `173244f4`
                             「월간 공휴일 이름의 브라우저 툴팁(title) 제거」(2026-09-09 사용자 결정)이고,
                             그것은 축소 세 커밋보다 **뒤**다(08-25 → 09-09) — 즉 «맞추기가 있는
                             상태에서» 지우라는 뜻이었다. 그 상태가 이제 됐다.

                        ② ⚠ 그래도 «영영 안 잘린다»는 뜻은 아니다 — 적어 둔다.
                           `min-width: 0` 이 1fr 균등분배를 지켜 주고 9px 축소가 폭을 줄이지만,
                           **아주 긴 이름 × 아주 좁은 칸**에서는 여전히 말줄임이 날 수 있다.
                           2026-09-28 실측(「개천절 대체공휴일」 · 10px 기준): 칸 폭 90 에서 잘렸다.
                           ⇒ ⛔ 그 자리를 «툴팁으로» 메우지 않는 것이 사용자 결정이다. 되살리려면
                             **새 결정을 받는다** — 이 주석을 근거로 되살리지 말 것.

                        ③ 되살릴 조건 — 위 넷 중 하나라도 빠지면.
                           특히 `min-width: 0` 은 **눈에 쓸모가 안 보여 지워지기 쉽다**(그 CSS 규칙
                           머리말에 그 경고를 적어 두었다). 그것이 빠지면 칸이 어긋나면서 ellipsis
                           가 안 돌고, 그때는 잘린 이름을 볼 길이 없다.
                           ⇒ ★ `monthview-canon-gap.guard.test.tsx` 의 간극 ③ ⓒ 가 그 둘을
                             **짝으로** 본다 — 한쪽만 되돌리면 빨개진다.

                        ④ 임계값은 그대로 7자다 — 「개천절 대체공휴일」은 «9자»라 걸린다.
                           (⚠ 2026-09-28 에 이 줄이 «8자»로 적혀 있었다 — 눈으로 센 수다.
                            2026-10-06 에 시험의 닻이 잡았다: '개천절 대체공휴일'.length === 9)
                      */}
                      {holidayName && (
                        <div
                          className={`calendar-month-holiday${holidayName.length > HOLIDAY_LONG_NAME_THRESHOLD ? ' calendar-month-holiday--long' : ''}`}
                        >
                          {holidayName}
                        </div>
                      )}
                      </div>
                      {onQuickCreate && (
                        <button
                          type="button"
                          className="calendar-month-cell-quickadd"
                          onClick={(ev) => { ev.stopPropagation(); onQuickCreate(new Date(d)); }}
                          aria-label={tCalendar('calendar.month.quick_create', { date: dateKey(d) })}
                          title={tCalendar('calendar.month.quick_create', { date: dateKey(d) })}
                        >
                          <Plus size={12} aria-hidden />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 전경 — 이벤트 바/칩 (pointer-events:none, 개별 버튼만 auto) */}
              <div className="calendar-month-week-fg">
                {segments.map((seg) => {
                  const e = seg.event;
                  const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
                  const color = meta?.color ?? 'var(--text-muted)';
                  /** 하루짜리 하루종일 — 채우지 않고 끝선도 없이 다른 일정과 같게(2026-10-19 사용자 지시). */
                  const singleAllDay = Boolean(e.isAllDay) && seg.colStart === seg.colEnd && !seg.continuesLeft && !seg.continuesRight;
                  const isDoneTodo = e.type === 'todo' && e.done;
                  /*
                   * ⭐ M-4 — 끌 수 있는 자리. **주간(`TimeGridView`)과 같은 권한 기준: 작성자만.**
                   * ⚠ `myUserId == null`(아직 못 읽었다)이면 ★ 막지 않는다 — 정본 그대로다.
                   *   서버가 어차피 권한을 다시 본다(화면은 «할 수 있는 일»을 줄일 뿐이다).
                   * ⚠ `onEventMove` 를 안 받으면 ⇒ `draggable` 이 아예 안 붙는다(종전과 100% 같다).
                   */
                  /*
                   * ⛔ 2026-10-05 사용자 결정 — **반복 일정은 끌 수 없다**(까닭·측정값은 `dragPolicy.ts`).
                   *   ⇒ `draggable` 이 안 붙고 `--draggable` 클래스도 안 붙어 **커서(grab)까지 함께 꺼진다**
                   *     ⇒ 「끌 수 있구나」로 오해할 자리가 남지 않는다.
                   */
                  const canDrag = !!onEventMove && !isDragBlocked(e) && (myUserId == null || e.creatorId === myUserId);
                  /*
                   * ⭐ 2026-09-09 사용자 지시 — 「헤더의 달이 아닌 날짜의 **일정도** 셀처럼 흐리게」.
                   *   ⚠ 칩은 **칸과 다른 레이어**(주 단위 격자)라 칸의 opacity 가 **안 닿는다**
                   *     ⇒ 그래서 따로 건다. 정본 `MonthView.tsx:244` 가 같은 말을 적는다.
                   *
                   * ⛔ 걸친 칩은 **시작일**로 정한다 — 한 칩이 ★ 지난 달과 이번 달에 «함께» 걸칠 수
                   *   있고(달이 바뀌는 그 주), 그때 「어느 달이냐」가 하나로 안 정해진다.
                   *   ⇒ ⭐ 정본이 고른 기준이 `week[seg.colStart]`(시작일)이고, **그대로** 쓴다
                   *     (재서 확인 — 내가 지어낸 규칙이 아니다).
                   */
                  const segDay = week[seg.colStart]!;
                  const segOtherMonth = segDay.getMonth() !== monthIdx || segDay.getFullYear() !== yearIdx;
                  const cls = [
                    'calendar-month-seg',
                    segOtherMonth ? 'calendar-month-seg--other-month' : '',
                    seg.isBar ? 'calendar-month-seg--bar' : 'calendar-month-seg--chip',
                    seg.continuesLeft ? 'calendar-month-seg--contl' : '',
                    seg.continuesRight ? 'calendar-month-seg--contr' : '',
                    isDoneTodo ? 'calendar-month-seg--done' : '',
                    isMineEvent(e, myUserId) ? 'calendar-month-seg--mine' : '',
                    canDrag ? 'calendar-month-seg--draggable' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <button
                      key={`${e.id}-${e.occurrenceAt ?? ''}-${seg.colStart}`}
                      type="button"
                      className={cls}
                      /*
                        ⭐ M-4 — 끄는 쪽(정본 그대로).
                        ⚠ 칩 자신도 «놓는 자리»다 — 칩 위에 놓으면 그 칩이 «앉아 있는 날»로 간다.
                          안 그러면 칩이 덮인 칸에서는 놓기가 안 먹는다(전경 레이어가 가린다).
                      */
                      draggable={canDrag}
                      onDragStart={(ev) => {
                        if (!canDrag) { ev.preventDefault(); return; }
                        draggingEventRef.current = e;
                        ev.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => { draggingEventRef.current = null; }}
                      onDragOver={(ev) => { if (draggingEventRef.current) ev.preventDefault(); }}
                      onDrop={(ev) => handleDropOnDay(week[seg.colStart]!, ev)}
                      style={{
                        gridColumn: `${seg.colStart + 1} / ${seg.colEnd + 2}`,
                        gridRow: seg.lane + 1,
                        /*
                          2026-09-09 사용자 결정 — 색 언어 통일: 바탕을 채우지 않고
                          «왼쪽 유형색 선»으로 말한다. 글자는 본문색(CSS)이라 읽기 쉽고,
                          내 것/남의 것은 굵기로 갈린다(--mine).
                        */
                        // ⭐ 2026-10-14 — 하루종일·여러 날(= 바)은 유형색으로 채운다(lib/longEventFill.ts).
                        // 2026-10-14 — 그 밖(하루 안 일정)은 바탕을 비운다(사용자 지시 「배경색을 없애줘」).
                        // ⭐ 2026-10-19 (사용자 지시) — «하루짜리» 하루종일만 채우지 않는다(다른 일정과 같게).
                        //   여러 날 일정은 하루종일이든 시간이 있든 연속 일정이라 종전처럼 채운다.
                        background: seg.isBar && !singleAllDay ? longEventFill(color, MONTH_LONG_EVENT_FILL_PCT) : 'transparent',
                        /*
                          ⭐ 2026-10-19 사용자 결정(끝 표시 시안 B) — 기간 막대는 «진짜 시작·끝»에 진한 끝선, 주를 넘어
                            이어지는 쪽은 선 없이 화살표 모양(CSS --contl/--contr). 하루 안 칩은 종전 그대로(왼쪽 유형색 선).
                        */
                        // 끝선 색은 하루 칩과 같은 유형색이다(2026-10-19 — 종전 «진하게 섞기»는 같은 회의인데 색이 달라 보여 걷었다).
                        borderLeft: seg.isBar && seg.continuesLeft ? 'none' : `3px solid ${color}`,
                        // 하루짜리 하루종일은 다른 일정과 똑같이 왼쪽 선만 — 끝선은 여러 날에 걸칠 때만(어디까지인지 보이게).
                        borderRight: seg.isBar && !seg.continuesRight && !singleAllDay ? `3px solid ${color}` : undefined,
                      }}
                      onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                      /* 2026-09-09 사용자 결정: 기본 title 툴팁 «대신» 스타일 툴팁(제목·유형·캘린더·작성자).
                         ⛔ title 을 남기면 «둘 다» 뜬다. 지운 유형 이름은 툴팁이 같은 출처에서 되살린다. */
                      {...tooltipOf(e)}
                    >
                      <EventCreatorAvatar event={e} className="calendar-month-seg-avatar" />
                      <span className="calendar-month-seg-ico" aria-hidden>
                        <TypeIcon type={e.type} size={12} />
                      </span>
                      {!e.isAllDay && !seg.isBar && (
                        <span className="calendar-month-seg-time">{formatTime(e.startAt)}</span>
                      )}
                      <span className="calendar-month-seg-title"><ReservationBadge event={e} hasChipTooltip />{e.title}</span>
                    </button>
                  );
                })}
                {/* 레인 초과 「+n개」 — 열별 · ⭐ 2026-10-19 마지막 줄 «자리»에 일정 칩과 같은 꼴로(종전: 그 아래 5번째 줄 · 칸 밖으로 잘림) */}
                {more.map((n, ci) =>
                  n > 0 ? (
                    <button
                      key={`more-${ci}`}
                      type="button"
                      /* ⭐ 「더보기」도 칸과 다른 레이어다 — 그 칸의 날짜로 정한다(걸치지 않는다). */
                      className={`calendar-month-more calendar-month-more--lane${
                        week[ci]!.getMonth() !== monthIdx || week[ci]!.getFullYear() !== yearIdx
                          ? ' calendar-month-more--other-month' : ''}`}
                      style={{ gridColumn: `${ci + 1} / ${ci + 2}`, gridRow: lanes }}
                      onClick={(ev) => fireDateClick(week[ci]!, ev)}
                    >
                      {tCalendar('calendar.month.more_count', { n })}
                    </button>
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
